/**
 * Interface for file handle storage that exactly matches the production schema:
 * 
 * interface DataframeDB extends DBSchema {
 *   'dataframes': {
 *     key: string // name
 *     value: {
 *       dataTableSource: string,
 *       handle: File
 *     },
 *     indexes: {'dataTableSource': string, 'unique-source': string}
 *   }
 * }
 */
export interface FileHandle {
  dataTableSource: string;
  handle: File;
}

/**
 * Abstract interface for file storage implementations
 * 
 * IMPORTANT: This implementation must match the schema used in production
 * where each entry has a name as the key and a value containing dataTableSource and handle (File object)
 */
export interface LocalFileStore {
  /**
   * Store a file handle
   * @param name Name of the file (key)
   * @param fileHandle FileHandle object containing dataTableSource and handle
   */
  storeFileHandle(name: string, fileHandle: FileHandle): Promise<void>;

  /**
   * Retrieve a file handle by name
   * @param name Name of the file (key)
   * @returns FileHandle object
   * @throws Error if file not found
   */
  getFileHandle(name: string): Promise<FileHandle>;

  /**
   * Retrieve a file handle by dataTableSource
   * @param source dataTableSource identifier
   * @returns FileHandle object
   * @throws Error if file not found
   */
  getFileHandleBySource(source: string): Promise<FileHandle>;

  /**
   * List all file handles
   * @returns Array of all file handles with their names
   */
  listFileHandles(): Promise<Array<{name: string, fileHandle: FileHandle}>>;

  /**
   * Delete a file by name
   * @param name Name of the file (key)
   */
  deleteFile(name: string): Promise<void>;

  /**
   * Delete a file by dataTableSource
   * @param source dataTableSource identifier
   */
  deleteFileBySource(source: string): Promise<void>;

  /**
   * Close the store and clean up resources
   */
  close(): Promise<void>;
}

/**
 * IndexedDB implementation of LocalFileStore
 * 
 * This implementation exactly matches the production schema:
 * interface DataframeDB extends DBSchema {
 *   'dataframes': {
 *     key: string // name
 *     value: {
 *       dataTableSource: string,
 *       handle: File
 *     },
 *     indexes: {'dataTableSource': string, 'unique-source': string}
 *   }
 * }
 */
export class IndexedDBFileStore implements LocalFileStore {
  private static readonly DB_NAME = 'dataframes';
  private static readonly STORE_NAME = 'dataframes';
  private static readonly DB_VERSION = 1;

  private db: IDBDatabase | null = null;

  /**
   * Initialize the IndexedDB database
   */
  private async initialize(): Promise<void> {
    if (this.db) return;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(IndexedDBFileStore.DB_NAME, IndexedDBFileStore.DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(IndexedDBFileStore.STORE_NAME)) {
          const store = db.createObjectStore(IndexedDBFileStore.STORE_NAME);
          store.createIndex('dataTableSource', 'dataTableSource', { unique: true });
          store.createIndex('unique-source', 'dataTableSource', { unique: true });
        }
      };
    });
  }

  /**
   * Store a file handle in IndexedDB
   * @param name Name of the file (key)
   * @param fileHandle FileHandle object containing dataTableSource and handle
   */
  async storeFileHandle(name: string, fileHandle: FileHandle): Promise<void> {
    await this.initialize();
    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([IndexedDBFileStore.STORE_NAME], 'readwrite');
      const store = transaction.objectStore(IndexedDBFileStore.STORE_NAME);

      const request = store.put(fileHandle, name);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  /**
   * Retrieve a file handle from IndexedDB by name
   * @param name Name of the file (key)
   * @returns FileHandle object
   */
  async getFileHandle(name: string): Promise<FileHandle> {
    await this.initialize();
    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([IndexedDBFileStore.STORE_NAME], 'readonly');
      const store = transaction.objectStore(IndexedDBFileStore.STORE_NAME);

      const request = store.get(name);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        if (!request.result) {
          reject(new Error(`File not found: ${name}`));
          return;
        }
        resolve(request.result);
      };
    });
  }

  /**
   * Retrieve a file handle from IndexedDB by dataTableSource
   * @param source dataTableSource identifier
   * @returns FileHandle object
   */
  async getFileHandleBySource(source: string): Promise<FileHandle> {
    await this.initialize();
    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([IndexedDBFileStore.STORE_NAME], 'readonly');
      const store = transaction.objectStore(IndexedDBFileStore.STORE_NAME);
      const index = store.index('unique-source');

      const request = index.get(source);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        if (!request.result) {
          reject(new Error(`File not found for source: ${source}`));
          return;
        }
        resolve(request.result);
      };
    });
  }

  /**
   * List all file handles in IndexedDB
   * @returns Array of all file handles with their names
   */
  async listFileHandles(): Promise<Array<{name: string, fileHandle: FileHandle}>> {
    await this.initialize();
    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([IndexedDBFileStore.STORE_NAME], 'readonly');
      const store = transaction.objectStore(IndexedDBFileStore.STORE_NAME);

      const result: Array<{name: string, fileHandle: FileHandle}> = [];
      const request = store.openCursor();
      
      request.onerror = () => reject(request.error);
      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result as IDBCursorWithValue | null;
        if (cursor) {
          result.push({
            name: cursor.key as string,
            fileHandle: cursor.value
          });
          cursor.continue();
        } else {
          resolve(result);
        }
      };
    });
  }

  /**
   * Delete a file from IndexedDB by name
   * @param name Name of the file (key)
   */
  async deleteFile(name: string): Promise<void> {
    await this.initialize();
    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([IndexedDBFileStore.STORE_NAME], 'readwrite');
      const store = transaction.objectStore(IndexedDBFileStore.STORE_NAME);

      const request = store.delete(name);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  /**
   * Delete a file from IndexedDB by dataTableSource
   * @param source dataTableSource identifier
   */
  async deleteFileBySource(source: string): Promise<void> {
    try {
      // First get the name using the source
      const transaction = this.db!.transaction([IndexedDBFileStore.STORE_NAME], 'readonly');
      const store = transaction.objectStore(IndexedDBFileStore.STORE_NAME);
      const index = store.index('unique-source');
      
      return new Promise((resolve, reject) => {
        const request = index.getKey(source);
        request.onerror = () => reject(request.error);
        request.onsuccess = async () => {
          if (!request.result) {
            // If file not found, consider it already deleted
            console.warn(`File with source ${source} not found for deletion`);
            resolve();
            return;
          }
          
          const name = request.result as string;
          await this.deleteFile(name);
          resolve();
        };
      });
    } catch (error) {
      console.warn(`Error deleting file with source ${source}:`, error);
    }
  }

  /**
   * Close the database connection
   */
  async close(): Promise<void> {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }
}

/**
 * In-memory implementation of LocalFileStore for testing
 * 
 * This implementation matches the same interface as the IndexedDBFileStore
 * but keeps everything in memory.
 */
export class InMemoryFileStore implements LocalFileStore {
  private files = new Map<string, FileHandle>();
  private sourceToNameMap = new Map<string, string>();

  async storeFileHandle(name: string, fileHandle: FileHandle): Promise<void> {
    this.files.set(name, fileHandle);
    this.sourceToNameMap.set(fileHandle.dataTableSource, name);
  }

  async getFileHandle(name: string): Promise<FileHandle> {
    const handle = this.files.get(name);
    if (!handle) {
      throw new Error(`File not found: ${name}`);
    }
    return handle;
  }

  async getFileHandleBySource(source: string): Promise<FileHandle> {
    const name = this.sourceToNameMap.get(source);
    if (!name) {
      throw new Error(`File not found for source: ${source}`);
    }
    return this.getFileHandle(name);
  }

  async listFileHandles(): Promise<Array<{name: string, fileHandle: FileHandle}>> {
    const result: Array<{name: string, fileHandle: FileHandle}> = [];
    for (const [name, fileHandle] of this.files.entries()) {
      result.push({ name, fileHandle });
    }
    return result;
  }

  async deleteFile(name: string): Promise<void> {
    const handle = this.files.get(name);
    if (handle) {
      this.sourceToNameMap.delete(handle.dataTableSource);
    }
    this.files.delete(name);
  }

  async deleteFileBySource(source: string): Promise<void> {
    const name = this.sourceToNameMap.get(source);
    if (name) {
      this.files.delete(name);
      this.sourceToNameMap.delete(source);
    }
  }

  async close(): Promise<void> {
    this.files.clear();
    this.sourceToNameMap.clear();
  }
} 